import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const UPLOAD_DOCUMENT_REQUEST = 'app/ProfileSettingsPage/UPLOAD_DOCUMENT_REQUEST';
export const UPLOAD_DOCUMENT_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_DOCUMENT_SUCCESS';
export const UPLOAD_DOCUMENT_ERROR = 'app/ProfileSettingsPage/UPLOAD_DOCUMENT_ERROR';

export const FETCH_FILE_URL_REQUEST = 'app/ProfileSettingsPage/FETCH_FILE_URL_REQUEST';
export const FETCH_FILE_URL_SUCCESS = 'app/ProfileSettingsPage/FETCH_FILE_URL_SUCCESS';
export const FETCH_FILE_URL_ERROR = 'app/ProfileSettingsPage/FETCH_FILE_URL_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// SDK method: documents.upload
export const uploadDocumentRequest = params => ({ type: UPLOAD_DOCUMENT_REQUEST, payload: { params } });
export const uploadDocumentSuccess = result => ({ type: UPLOAD_DOCUMENT_SUCCESS, payload: result.data });
export const uploadDocumentError = error => ({
  type: UPLOAD_DOCUMENT_ERROR,
  payload: error,
  error: true,
});

// SDK method: images.show - fetch file URL
export const fetchFileUrlRequest = params => ({ type: FETCH_FILE_URL_REQUEST, payload: { params } });
export const fetchFileUrlSuccess = result => ({ type: FETCH_FILE_URL_SUCCESS, payload: result.data });
export const fetchFileUrlError = error => ({
  type: FETCH_FILE_URL_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
}

// Upload document files to Sharetribe
export function uploadDocument(actionPayload) {
  return (dispatch, getState, sdk) => {
    const { id, file, fieldName } = actionPayload;
    dispatch(uploadDocumentRequest(actionPayload));

    const bodyParams = {
      image: file, // Sharetribe's image upload can handle various file types
    };
    const queryParams = {
      expand: true,
      // Request image variants to get proper URLs
      'fields.image': ['variants.default', 'variants.scaled-small', 'variants.scaled-medium', 'variants.scaled-large'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedFile = resp.data.data;
        console.log('🔍 Sharetribe upload response:', {
          id: uploadedFile.id.uuid,
          attributes: uploadedFile.attributes,
          relationships: uploadedFile.relationships
        });
        
        // Extract URL from variants or attributes
        let fileUrl = null;
        if (uploadedFile.attributes?.variants?.default?.url) {
          fileUrl = uploadedFile.attributes.variants.default.url;
        } else if (uploadedFile.attributes?.variants?.['scaled-large']?.url) {
          fileUrl = uploadedFile.attributes.variants['scaled-large'].url;
        } else if (uploadedFile.attributes?.url) {
          fileUrl = uploadedFile.attributes.url;
        } else {
          // Generate a Sharetribe console URL pattern (this may need adjustment based on your setup)
          fileUrl = `https://console.sharetribe.com/images/${uploadedFile.id.uuid}`;
          console.log('⚠️ No direct URL found in response, using console URL pattern');
        }
        
        const fileMetadata = {
          id: uploadedFile.id.uuid,
          name: file.name,
          type: file.type,
          size: file.size,
          uploaded: true,
          uploadedAt: new Date().toISOString(),
          sharetribeImageId: uploadedFile.id.uuid,
          url: fileUrl,
          fieldName: fieldName
        };
        
        console.log('✅ File uploaded with metadata:', fileMetadata);
        dispatch(uploadDocumentSuccess({ data: { id, fileMetadata } }));
        return fileMetadata;
      })
      .catch(e => {
        dispatch(uploadDocumentError({ id, fieldName, error: storableError(e) }));
        throw e;
      });
  };
}

// Fetch file URL from Sharetribe by file ID
export function fetchFileUrl(fileId) {
  return (dispatch, getState, sdk) => {
    dispatch(fetchFileUrlRequest({ fileId }));

    const queryParams = {
      'fields.image': ['variants.default', 'variants.scaled-large', 'variants.scaled-medium']
    };

    return sdk.images
      .show({ id: fileId, ...queryParams })
      .then(resp => {
        const imageData = resp.data.data;
        console.log('📁 Sharetribe file response:', imageData);
        
        // Try to extract URL from different variants
        let fileUrl = null;
        if (imageData.attributes?.variants?.default?.url) {
          fileUrl = imageData.attributes.variants.default.url;
        } else if (imageData.attributes?.variants?.['scaled-large']?.url) {
          fileUrl = imageData.attributes.variants['scaled-large'].url;
        } else if (imageData.attributes?.variants?.['scaled-medium']?.url) {
          fileUrl = imageData.attributes.variants['scaled-medium'].url;
        } else if (imageData.attributes?.url) {
          fileUrl = imageData.attributes.url;
        }
        
        dispatch(fetchFileUrlSuccess({ data: { fileId, fileUrl } }));
        return fileUrl;
      })
      .catch(e => {
        dispatch(fetchFileUrlError({ fileId, error: storableError(e) }));
        throw e;
      });
  };
}
